import React, { useMemo } from "react";
import "./roadClosureWorkPermitAll.css";

const ExcavationPermitForm = ({ departmentApprovals = [] }) => {
  const workDetails = useMemo(() => {
    const toDisplayValue = (value, fieldType) => {
      if (fieldType === "boolean") {
        return value === true || value === "true" ? "Yes" : "";
      }

      return value || "";
    };

    const normalizeKey = (value = "") =>
      value.toString().trim().toLowerCase();

    const buildInputMap = (inputs = []) => {
      const inputMap = new Map();

      inputs.forEach((input) => {
        const fieldValue = toDisplayValue(input.field_value, input.field_type);
        const keys = [input.field_name, input.field_label]
          .filter(Boolean)
          .map((key) => normalizeKey(key));

        keys.forEach((key) => {
          inputMap.set(key, fieldValue);
        });
      });

      return inputMap;
    };

    const getInputValue = (inputMap, ...keys) => {
      for (const key of keys) {
        const normalizedKey = normalizeKey(key);
        if (inputMap.has(normalizedKey)) {
          return inputMap.get(normalizedKey);
        }
      }

      return "";
    };

    const details = {
      electricalCables: "",
      electricalIsolation: "",
      electricalDate: "",
      electricalName: "",
      electricalSignature: "",
      instrumentCables: "",
      instrumentPrecautions: "",
      instrumentDate: "",
      instrumentName: "",
      instrumentSignature: "",
      mechanicalDescription: "",
      mechanicalPrecautions: "",
      mechanicalDate: "",
      mechanicalName: "",
      mechanicalSignature: "",
      crossOver: "",
      barricades: "",
      roadBlocks: "",
      shoring: "",
      others: "",
      excavationType: "",
      gasMasks: "",
      eyeProtection: "",
      earProtection: "",
      woodenTools: "",
      safetyOthers: "",
      emergencyApproach: "",
      otherPrecautions: "",
    };

    departmentApprovals.forEach((dept) => {
      const name = dept.department_name?.toLowerCase() || "";
      const inputs = dept.inputs || [];
      const inputMap = buildInputMap(inputs);

      if (name.includes("elect")) {
        details.electricalCables = getInputValue(
          inputMap,
          "Details of underground cables present in the area to be excavated",
          "underground cables",
          "underground cable"
        );
        details.electricalIsolation = getInputValue(
          inputMap,
          "Electrical isolation required",
          "Electrical isolation of following required",
          "electrical isolation"
        );
        details.electricalDate = getInputValue(inputMap, "Date");
        details.electricalName = getInputValue(
          inputMap,
          "Name & Designation",
          "Name"
        );
        details.electricalSignature = getInputValue(inputMap, "Signature");
      }

      if (name.includes("mech")) {
        details.mechanicalDescription = getInputValue(
          inputMap,
          "Description of underground facilities existing at the proposed location",
          "Description"
        );
        details.mechanicalPrecautions = getInputValue(
          inputMap,
          "Precautions to be observed for safe excavation job",
          "Precautions"
        );
        details.mechanicalDate = getInputValue(inputMap, "Date");
        details.mechanicalName = getInputValue(
          inputMap,
          "Name & Designation",
          "Name"
        );
        details.mechanicalSignature = getInputValue(inputMap, "Signature");
      }

      if (name.includes("instrument") || name.includes("it")) {
        details.instrumentCables = getInputValue(
          inputMap,
          "Details of underground cables present in the area to be excavated",
          "underground cables"
        );
        details.instrumentPrecautions = getInputValue(
          inputMap,
          "Precautions to be observed for safe excavation job",
          "Precautions"
        );
        details.instrumentDate = getInputValue(inputMap, "Date");
        details.instrumentName = getInputValue(
          inputMap,
          "Name & Designation",
          "Name"
        );
        details.instrumentSignature = getInputValue(inputMap, "Signature");
      }

      if (name.includes("fire") || name.includes("safety")) {
        const emergencySelections = [];
        const isChecked = (...keys) =>
          keys.some((key) => {
            const normalizedKey = normalizeKey(key);
            return (
              inputMap.get(normalizedKey) === "Yes" ||
              inputMap.get(normalizedKey) === true
            );
          });

        details.crossOver = getInputValue(inputMap, "Cross over");
        details.barricades = getInputValue(inputMap, "Barricades");
        details.roadBlocks = getInputValue(inputMap, "Road blocks");
        details.shoring = getInputValue(inputMap, "Shoring");
        details.others = getInputValue(inputMap, "Others");
        details.excavationType = getInputValue(
          inputMap,
          "excavation_type",
          "Types of excavation",
          "Types of Excavation"
        );
        details.gasMasks = getInputValue(inputMap, "Gas Masks");
        details.eyeProtection = getInputValue(inputMap, "Eye Protection");
        details.earProtection = getInputValue(inputMap, "Ear Protection");
        details.woodenTools = getInputValue(
          inputMap,
          "Wooden Handheld tools",
          "Wooden"
        );
        details.safetyOthers = getInputValue(inputMap, "Safety Others");
        details.otherPrecautions = getInputValue(
          inputMap,
          "Other precautions",
          "Other Precautions"
        );

        if (isChecked("Emergency approach for vehicles", "Emergency")) {
          emergencySelections.push("Emergency approach for vehicles");
        }

        if (isChecked("Barricading")) {
          emergencySelections.push("Barricading");
        }

        if (isChecked("Road closure")) {
          emergencySelections.push("Road closure");
        }

        if (emergencySelections.length > 0) {
          details.emergencyApproach = emergencySelections.join(", ");
        }
      }
    });

    return details;
  }, [departmentApprovals]);

  const DottedUnderline = ({ children, value = "" }) => (
    <div className="rc_dotted-line-wrapper">
      <span className="rc_field-label">{children}</span>
      <span className="rc_dotted-underline">{value}</span>
    </div>
  );

  const SignatureLine = ({
    dateValue = "",
    nameValue = "",
    signatureValue = "",
  }) => (
    <div className="rc_signature-line">
      <div className="rc_signature-field rc_date-field">
        <span className="rc_field-label">Date</span>
        <span className="rc_signature-value">{dateValue}</span>
      </div>
      <div className="rc_signature-field rc_name-field">
        <span className="rc_field-label">Name & Designation</span>
        <span className="rc_signature-value rc_name-value">{nameValue}</span>
      </div>
      <div className="rc_signature-field rc_sign-field">
        <span className="rc_field-label">Signature</span>
        <span className="rc_signature-value">{signatureValue}</span>
      </div>
    </div>
  );

  const Section = ({ title, children }) => (
    <div className="rc_section-container">
      <h3 className="rc_section-title">{title}</h3>
      {children}
      <div className="rc_department-divider" aria-hidden="true" />
    </div>
  );

  return (
    <div className="rc_excavation-form">
      <Section title="I. ELECTRICAL DEPARTMENT:">
        <DottedUnderline value={workDetails.electricalCables}>
          1. Details of underground cables present in the area to be excavated
        </DottedUnderline>
        <DottedUnderline value={workDetails.electricalIsolation}>
          2. Electrical isolation of following required
        </DottedUnderline>
        <SignatureLine
          dateValue={workDetails.electricalDate}
          nameValue={workDetails.electricalName}
          signatureValue={workDetails.electricalSignature}
        />
      </Section>

      <Section title="II. INSTRUMENT / IT DEPARTMENT:">
        <DottedUnderline value={workDetails.instrumentCables}>
          1. Details of underground cables present in the area to be excavated
        </DottedUnderline>
        <DottedUnderline value={workDetails.instrumentPrecautions}>
          2. Precautions to be observed for safe excavation job
        </DottedUnderline>
        <SignatureLine
          dateValue={workDetails.instrumentDate}
          nameValue={workDetails.instrumentName}
          signatureValue={workDetails.instrumentSignature}
        />
      </Section>

      <Section title="III. MECHANICAL DEPARTMENT:">
        <DottedUnderline value={workDetails.mechanicalDescription}>
          1. Description of underground facilities existing at the proposed
          location
        </DottedUnderline>
        <DottedUnderline value={workDetails.mechanicalPrecautions}>
          2. Precautions to be observed for safe excavation job
        </DottedUnderline>
        <SignatureLine
          dateValue={workDetails.mechanicalDate}
          nameValue={workDetails.mechanicalName}
          signatureValue={workDetails.mechanicalSignature}
        />
      </Section>

      <Section title="IV. FIRE & SAFETY DEPARTMENT:">
        <div className="rc_field-group">
          <div className="rc_inline-fields">
            <span className="rc_field-label">1. Provide: </span>
            <div className="rc_inline-field">
              <span className="rc_field-label">Cross over</span>
              <span className="rc_inline-underline">
                {workDetails.crossOver}
              </span>
            </div>
            <div className="rc_inline-field">
              <span className="rc_field-label">Barricades</span>
              <span className="rc_inline-underline">
                {workDetails.barricades}
              </span>
            </div>
            <div className="rc_inline-field">
              <span className="rc_field-label">Road blocks</span>
              <span className="rc_inline-underline">
                {workDetails.roadBlocks}
              </span>
            </div>
            <div className="rc_inline-field">
              <span className="rc_field-label">Shoring</span>
              <span className="rc_inline-underline">{workDetails.shoring}</span>
            </div>
          </div>
          <DottedUnderline value={workDetails.others}>Others</DottedUnderline>
        </div>

        <DottedUnderline value={workDetails.excavationType}>
          2. Types of Excavation: Manual/Mechanical/Hand tools/Others
        </DottedUnderline>

        <div className="rc_field-group">
          <div className="rc_inline-fields">
            <span className="rc_field-label">
              3. Use following safety equipment&apos;s:{" "}
            </span>
            <div className="rc_inline-field">
              <span className="rc_field-label">Gas Masks</span>
              <span className="rc_inline-underline">
                {workDetails.gasMasks}
              </span>
            </div>
            <div className="rc_inline-field">
              <span className="rc_field-label">Eye Protection</span>
              <span className="rc_inline-underline">
                {workDetails.eyeProtection}
              </span>
            </div>
            <div className="rc_inline-field">
              <span className="rc_field-label">Ear Protection</span>
              <span className="rc_inline-underline">
                {workDetails.earProtection}
              </span>
            </div>
          </div>

          <div className="rc_inline-fields">
            <div className="rc_inline-field">
              <span className="rc_field-label">Wooden Handheld tools</span>
              <span className="rc_inline-underline">
                {workDetails.woodenTools}
              </span>
            </div>
            <div className="rc_inline-field rc_flex-grow">
              <span className="rc_field-label">Others</span>
              <span className="rc_inline-underline rc_flex-underline">
                {workDetails.safetyOthers}
              </span>
            </div>
          </div>
        </div>

        <DottedUnderline value={workDetails.emergencyApproach}>
          4. Provide following: Approach for the Emergency Vehicles/Road Closure
          boards/Barricading tapes
        </DottedUnderline>

        <DottedUnderline value={workDetails.otherPrecautions}>
          5. Other Precautions
        </DottedUnderline>
      </Section>

      <div className="rc_additional-precautions">
        <DottedUnderline>Additional Precautions, if any:</DottedUnderline>
        <div className="rc_blank-line" />
        <div className="rc_blank-line" />
      </div>
    </div>
  );
};

export default ExcavationPermitForm;
